import { AxiosError, CanceledError, type AxiosRequestConfig } from "axios";
import {
  ApiResponseRefusalError,
  BadRequestError,
  DomainRuleError,
  InternalServerError,
  NotFoundError,
  RequestRefusedError,
  TooManyRequestsError,
} from "./Errors";
import axiosInstance from "./axiosInstance";

type DomainErrorConfig = Record<string, new () => Error>;

const text = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/**
 * Core's reason for a 400, in whichever shape the endpoint answered: the endpoint's own words
 * (a string, or `{ error }`), the validation filter's failures (`[{ errorMessage }]`), or a
 * validation problem (`{ errors: { field: [message] } }`).
 */
const refusalReason = (data: unknown): string | undefined => {
  const join = (messages: (string | undefined)[]) =>
    messages.filter(Boolean).join(" ") || undefined;
  if (Array.isArray(data)) {
    return join(
      data.map(
        (failure) =>
          text(failure) ??
          text((failure as { errorMessage?: unknown })?.errorMessage),
      ),
    );
  }
  if (data && typeof data === "object") {
    const { error, errors, detail } = data as {
      error?: unknown;
      errors?: unknown;
      detail?: unknown;
    };
    if (text(error)) {
      return text(error);
    }
    if (errors && typeof errors === "object" && !Array.isArray(errors)) {
      const reason = join(Object.values(errors).flat().map(text));
      if (reason) {
        return reason;
      }
    }
    return text(detail);
  }
  return text(data);
};

type Config = AxiosRequestConfig & {
  domainErrors?: DomainErrorConfig;
  preserveRefusals?: boolean;
};

export class ApiBase {
  async #wrapWithTryCatch<ResponseType>(
    promise: Promise<ResponseType>,
    domainErrors?: DomainErrorConfig,
    preserveRefusals = false,
  ) {
    try {
      return await promise;
    } catch (error) {
      if (error instanceof CanceledError) {
        throw error;
      }

      console.error(error);

      if (error instanceof AxiosError) {
        if (
          preserveRefusals &&
          [403, 409, 503].includes(error.response?.status ?? 0)
        ) {
          const data = error.response?.data;
          const listed = Array.isArray(data?.errors) ? data.errors : [];
          const reason =
            listed
              .map((e: { reason?: string }) => e.reason)
              .filter(Boolean)
              .join(" ") || refusalReason(data);
          const mapped = listed.find(
            (e: { code?: string }) => domainErrors?.[e.code ?? ""],
          );
          if (reason && mapped) {
            const DomainError = domainErrors![mapped.code]!;
            const refusal = new DomainError();
            refusal.message = reason;
            throw refusal;
          }
          if (reason)
            throw new ApiResponseRefusalError(
              listed[0]?.code ?? data?.code ?? "",
              reason,
              data?.latest_draft_id,
            );
        }
        switch (error.response?.status) {
          case 422: {
            const errors: { code?: string; reason?: string; field?: string }[] =
              error.response?.data?.errors ?? [];
            for (const domainError of errors) {
              const DomainError = domainErrors?.[domainError.code ?? ""];
              if (DomainError) {
                const refusal = new DomainError();
                if (preserveRefusals && domainError.reason)
                  refusal.message = domainError.reason;
                throw refusal;
              }
            }

            // No mapping: keep Core's reason rather than drop it, and each
            // refusal with the field it names, for a screen that says it there.
            const reason = errors
              .map((e) => e.reason)
              .filter(Boolean)
              .join(" ");
            if (reason) {
              throw new DomainRuleError(
                errors[0]?.code ?? "",
                reason,
                errors
                  .filter((e) => text(e.reason))
                  .map((e) => ({
                    code: e.code ?? "",
                    reason: text(e.reason)!,
                    ...(text(e.field) ? { field: text(e.field) } : {}),
                  })),
              );
            }
            throw new InternalServerError();
          }
          case 409: {
            // A conflict with where something stands. The call's own error when the body names
            // a code it maps; otherwise the generic error, as every 409 got before.
            const conflicts: { code?: string }[] =
              error.response?.data?.errors ?? [];
            for (const conflict of conflicts) {
              const DomainError = domainErrors?.[conflict.code ?? ""];
              if (DomainError) {
                throw new DomainError();
              }
            }
            throw new InternalServerError();
          }
          case 503: {
            // A service Core depends on did not answer. The call's own error when the body
            // names a code it maps; otherwise the generic error, as every 503 got before.
            const listed: unknown = error.response?.data?.errors;
            const unavailable: { code?: string }[] = Array.isArray(listed)
              ? listed
              : [];
            for (const refusal of unavailable) {
              const DomainError = domainErrors?.[refusal.code ?? ""];
              if (DomainError) {
                throw new DomainError();
              }
            }
            throw new InternalServerError();
          }
          case 404:
            throw new NotFoundError();
          case 400: {
            // Keep Core's reason when it gave one, as for a 422.
            const reason = refusalReason(error.response?.data);
            throw reason
              ? new RequestRefusedError(reason)
              : new BadRequestError();
          }
          case 429:
            throw new TooManyRequestsError();
          default:
            throw new InternalServerError();
        }
      }

      throw new InternalServerError();
    }
  }

  protected async get<ResponseType>(
    url: string,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance.get<ResponseType>(url, options).then((res) => res.data),
      options?.domainErrors,
      options?.preserveRefusals,
    );
  }

  protected async post<RequestType, ResponseType>(
    url: string,
    data: RequestType,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance
        .post<ResponseType>(url, data, options)
        .then((res) => res.data),
      options?.domainErrors,
      options?.preserveRefusals,
    );
  }

  protected async put<RequestType, ResponseType>(
    url: string,
    data: RequestType,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance
        .put<ResponseType>(url, data, options)
        .then((res) => res.data),
      options?.domainErrors,
      options?.preserveRefusals,
    );
  }

  protected async patch<RequestType, ResponseType>(
    url: string,
    data: RequestType,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance
        .patch<ResponseType>(url, data, options)
        .then((res) => res.data),
      options?.domainErrors,
      options?.preserveRefusals,
    );
  }

  protected async delete<ResponseType>(
    url: string,
    options?: Config,
  ): Promise<ResponseType> {
    return this.#wrapWithTryCatch(
      axiosInstance.delete<ResponseType>(url, options).then((res) => res.data),
      options?.domainErrors,
      options?.preserveRefusals,
    );
  }
}
